// ==========================================================================
// Remote i18n catalog - runtime locale loading (ENG-949)
// ==========================================================================
//
// Non-English locales load at runtime from the engine's unified catalog
// endpoint, so adding or editing a translation is a content change rather
// than a frontend release. English stays compiled in (see ./index.ts) and is
// never fetched: rendering must never depend on a live request, because Yaya
// serves low-bandwidth users.
//
// Fetched catalogs are cached in localStorage and used immediately on the
// next boot, so a locale only needs one successful fetch, ever.

import config from '@/lib/config';

import type { Language } from './languages';

const CACHE_PREFIX = 'i18n.catalog.';
const FETCH_TIMEOUT_MS = 8000;

type CatalogResponse = {
  locale: string;
  version: string;
  pwa: Record<string, unknown>;
};

type CachedCatalog = {
  version: string;
  resources: Record<string, unknown>;
};

const cacheKey = (locale: Language): string => `${CACHE_PREFIX}${locale}`;

export function readCachedCatalog(locale: Language): CachedCatalog | null {
  try {
    const raw = localStorage.getItem(cacheKey(locale));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CachedCatalog;

    // Validate the shape, not just truthiness: a tampered or format-drifted
    // entry (e.g. resources: "x") would otherwise be seeded into i18next and
    // corrupt its lookup tree. A bad entry is treated as no cache.
    const validShape =
      parsed !== null &&
      typeof parsed === 'object' &&
      typeof parsed.version === 'string' &&
      parsed.resources !== null &&
      typeof parsed.resources === 'object';

    return validShape ? parsed : null;
  } catch {
    // Corrupt or unreadable cache is equivalent to no cache — the bundled
    // English fallback still renders.
    return null;
  }
}

function writeCachedCatalog(locale: Language, catalog: CachedCatalog): void {
  try {
    localStorage.setItem(cacheKey(locale), JSON.stringify(catalog));
  } catch {
    // Quota or private-mode failures are non-fatal: the catalog is already
    // live in memory for this session, it just won't survive a reload.
  }
}

/**
 * Fetch a locale's PWA catalog from the engine. Sends the cached version as an
 * ETag so an unchanged catalog costs a 304 rather than a full payload.
 * Resolves to null when the network fails or the locale is unknown upstream.
 */
export async function fetchCatalog(locale: Language): Promise<Record<string, unknown> | null> {
  const cached = readCachedCatalog(locale);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${config.apiUrl}/i18n/${locale}`, {
      headers: cached ? { 'If-None-Match': `"${cached.version}"` } : {},
      // Always revalidate rather than letting the HTTP cache answer offline
      // from a stale copy — our own localStorage cache is the offline path,
      // and it is the one that survives a page load.
      cache: 'no-cache',
      signal: controller.signal,
    });

    if (response.status === 304) {
      return cached?.resources ?? null;
    }

    if (!response.ok) {
      return cached?.resources ?? null;
    }

    const body = (await response.json()) as CatalogResponse;

    if (!body.pwa || Object.keys(body.pwa).length === 0) {
      return cached?.resources ?? null;
    }

    // Only persist with a real version. Caching `version: undefined` would make
    // the next boot send `If-None-Match: "undefined"`, which never matches, so
    // 304 revalidation would silently never engage and every load would
    // re-download the full payload. Without a version, use it this session only.
    if (typeof body.version === 'string' && body.version !== '') {
      writeCachedCatalog(locale, { version: body.version, resources: body.pwa });
    }

    return body.pwa;
  } catch {
    return cached?.resources ?? null;
  } finally {
    clearTimeout(timeout);
  }
}
